import React, { useRef, useState } from 'react';
import { ChevronLeft, Camera, ImageIcon } from 'lucide-react';
import Button from '../ui/Button';
import ImageUploader from '../ui/ImageUploader';
import { useRecordForm } from '../../hooks/useRecordForm';
import { useToast } from '../../hooks/useToast';
import { PermissionGuard } from '../auth/PermissionGuard';
import { ButtonPermissions } from '../../types/permission';

/**
 * 编辑上课记录表单
 */
const RecordFormEdit = ({ initialData = {}, onBack, onSave, onDelete, isSaving = false }) => {
  const toast = useToast();
  
  // 图片状态管理
  const [sessionArtworkPhotos, setSessionArtworkPhotos] = useState(initialData.session_artwork_photos_urls || []);
  const [classroomPhotos, setClassroomPhotos] = useState(initialData.classroom_photos_urls || []);
  
  const {
    students,
    courses,
    selectedStudentId,
    selectedCourseId,
    remainingHours,
    loading,
    getTodayDate,
    handleStudentChange,
    handleCourseChange,
  } = useRecordForm(initialData);

  // 表单字段 refs
  const dateRef = useRef(null);
  const studentIdRef = useRef(null);
  const courseIdRef = useRef(null);
  const consumedHoursRef = useRef(null);
  const remarksRef = useRef(null);

  // 转换日期格式：后端返回 "2025/12/22 00:00:00"，需要转为 "2025-12-22"
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return getTodayDate();
    // 移除时间部分，只保留日期
    const datePart = dateStr.split(' ')[0];
    // 替换 / 为 -
    return datePart.replace(/\//g, '-');
  };

  // 处理保存
  const handleSave = () => {
    // 注意：防重入锁由父组件 App.jsx 的 isSavingRef 统一管理，此处不重复判断

    // 收集表单数据
    const formData = {
      id: initialData.id,
      date: dateRef.current?.value || '',
      student_id: parseInt(studentIdRef.current?.value) || null,
      course_id: parseInt(courseIdRef.current?.value) || null,
      teacher: '夏天老师', // 默认值：夏天老师
      consumed_hours: parseFloat(consumedHoursRef.current?.value) || 0,
      remarks: remarksRef.current?.value || '',
      session_artwork_photos_urls: sessionArtworkPhotos,
      classroom_photos_urls: classroomPhotos,
    };

    // 验证必填字段
    if (!formData.date) {
      toast.error('请选择上课日期');
      return;
    }
    if (!formData.student_id) {
      toast.error('请选择学生');
      return;
    }
    if (!formData.course_id) {
      toast.error('请选择课程');
      return;
    }
    if (!formData.consumed_hours || formData.consumed_hours <= 0) {
      toast.error('请输入消耗课时');
      return;
    }

    console.log('[RecordFormEdit] 保存数据:', formData);
    onSave(formData);
  };

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      <header className="h-[56px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 -ml-1 text-[#2D3748]" disabled={isSaving}>
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-[#2D3748]">编辑记录</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
        {/* 基本信息 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          {/* 上课日期 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              上课日期
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={dateRef}
              type="date" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]" 
              defaultValue={formatDateForInput(initialData.date)}
              required
              disabled={isSaving}
            />
          </div>
          
          {/* 学生姓名 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              学生姓名
              <span className="text-red-500">*</span>
            </label>
            <select 
              ref={studentIdRef}
              className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right"
              value={selectedStudentId}
              onChange={(e) => handleStudentChange(e.target.value)}
              required
              disabled={isSaving}
            >
              <option value="">请选择学生</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* 课程名称（编辑模式下只读） */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              课程名称
            </label>
            <input type="hidden" ref={courseIdRef} value={selectedCourseId} />
            <div className="flex-1 text-sm text-right text-[#2D3748]">
              {initialData.course_name ? initialData.course_name.split(' | ')[0] : '未知课程'}
            </div>
          </div>
          
          {/* 消耗课时 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-24 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              消耗课时
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={consumedHoursRef}
              type="number" 
              placeholder="1" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]" 
              defaultValue={initialData.consumed_hours || "1"}
              min="0"
              step={initialData.use_reward_units ? "1" : "0.5"}
              required
              disabled={isSaving}
            />
          </div>
          
          {/* 课时类型（只读，仅赠送记录显示） */}
          {initialData.use_reward_units && (
            <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7] bg-blue-50/10">
              <label className="w-24 text-sm font-medium text-[#4A5568]">
                课时来源
              </label>
              <div className="flex-1 text-sm text-right font-medium text-[#3182CE]">
                赠送课时 (仅支持整数)
              </div>
            </div>
          )}
          
          {/* 剩余课时（只读） */}
          <div className="flex items-center px-4 py-3">
            <label className="w-24 text-sm font-medium text-[#4A5568]">
              剩余课时
            </label>
            <div className="flex-1 text-sm text-right text-[#3182CE] font-bold">
              {loading ? '计算中...' : remainingHours}
            </div>
          </div>
        </div>

        {/* 作品图片上传 */}
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <label className="text-sm font-medium text-[#4A5568] flex items-center gap-2">
              <ImageIcon size={16} className="text-[#3182CE]" />
              作品图片
            </label>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
            <ImageUploader
              value={sessionArtworkPhotos}
              onChange={setSessionArtworkPhotos}
              maxFiles={5}
              disabled={isSaving}
              uploadMeta={{
                imageType: '作品',
                studentId: String(selectedStudentId || initialData.student_id || ''),
                studentName: students.find(s => s.id === (selectedStudentId || initialData.student_id))?.name || initialData.student_name || '',
                courseId: String(selectedCourseId || initialData.course_id || ''),
                courseName: courses.find(c => c.id === (selectedCourseId || initialData.course_id))?.name || initialData.course_name || '',
                teacherName: '夏天老师'
              }}
            />
          </div>
        </div>

        {/* 课堂照片上传 */}
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <label className="text-sm font-medium text-[#4A5568] flex items-center gap-2">
              <Camera size={16} className="text-[#3182CE]" />
              课堂照片
            </label>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EDF2F7] shadow-sm">
            <ImageUploader
              value={classroomPhotos}
              onChange={setClassroomPhotos}
              maxFiles={10}
              disabled={isSaving}
              uploadMeta={{
                imageType: '课堂',
                studentId: String(selectedStudentId || initialData.student_id || ''),
                studentName: students.find(s => s.id === (selectedStudentId || initialData.student_id))?.name || initialData.student_name || '',
                courseId: String(selectedCourseId || initialData.course_id || ''),
                courseName: courses.find(c => c.id === (selectedCourseId || initialData.course_id))?.name || initialData.course_name || '',
                teacherName: '夏天老师'
              }}
            />
          </div>
        </div>

        {/* 作品评价 */}
        <div>
          <label className="text-sm font-medium text-[#4A5568] mb-2 block px-1">作品评价</label>
          <textarea 
            ref={remarksRef}
            className="w-full bg-white border border-[#EDF2F7] rounded-xl p-3 text-sm h-32 focus:outline-none focus:border-[#3182CE] disabled:bg-gray-50" 
            placeholder="请输入对学生本次表现的评价..."
            defaultValue={initialData.remarks || ""}
            disabled={isSaving}
          />
        </div>

        {/* 时间戳信息 - 仅编辑模式显示 */}
        {initialData.created_at && (
          <div className="bg-white rounded-xl border border-[#EDF2F7] p-4 text-xs text-[#718096] space-y-2">
            <div className="flex justify-between">
              <span>创建时间</span>
              <span>{initialData.created_at}</span>
            </div>
            {initialData.updated_at && (
              <div className="flex justify-between">
                <span>修改时间</span>
                <span>{initialData.updated_at}</span>
              </div>
            )}
          </div>
        )}
      </main>

      {/* 底部按钮区 - 编辑模式 */}
      <div className="h-[80px] bg-white border-t border-[#EDF2F7] px-6 flex items-center justify-between flex-shrink-0 z-10">
        <PermissionGuard permission={ButtonPermissions.attendance.delete}>
          <Button variant="danger" className="w-24" onClick={onDelete} disabled={isSaving}>作废</Button>
        </PermissionGuard>
        <Button variant="secondary" onClick={onBack} disabled={isSaving}>返回</Button>
        <PermissionGuard permission={ButtonPermissions.attendance.edit}>
          <Button variant="primary" className="w-24" onClick={handleSave} disabled={isSaving}>
            {isSaving ? '保存中...' : '保存'}
          </Button>
        </PermissionGuard>
      </div>
    </div>
  );
};

export default RecordFormEdit;
